import { fmtDate, fmtDateTime, toDateInputValue } from '@/lib/format-date';
import React from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Wifi, WifiOff, Radio, Wrench, BadgeCheck, ClipboardCheck, Droplet, RefreshCw,
  ChevronDown, ChevronRight, Printer,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { cn, publicAsset } from '@/lib/utils';
import { useUserActions } from '@/hooks/use-user-actions';
import { usePreferences } from '@/hooks/use-preferences';
import { LookupField } from '@/components/shared/lookup-field';

// ---- Service Report types — mirror the /dashboard/service-report response.
type CellStatus = 'overdue' | 'upcoming' | 'ok';
type OilBucket = 'oil1' | 'oil2' | 'oil3' | 'oil4' | 'oil5';

interface ScheduleCol {
  scheduleId: string;
  scheduleName: string;
  unitOfMeasurement: string | null;
  serviceInterval: number | null;
  isEngineOil: boolean;
}
interface ScheduleCell {
  lastValue: number | null;
  lastDate: string | null;
  nextValue: number | null;
  nextDate: string | null;
  delta: number | null;
  status: CellStatus;
  /** A satisfying service is completed but still awaiting approval — the due
   *  date above reflects only approved work. Shown as a "pending approval" hint. */
  pendingApproval?: boolean;
  /** An unapproved service exists for this asset+schedule. `lastDate` counts
   *  approved work only, so this is shown as a separate hint rather than
   *  standing in for a completed service — a draft nobody approves leaves the
   *  schedule overdue indefinitely, and that is the actionable fact. */
  draftDate?: string | null;
  draftJobSheet?: string | null;
}
interface OilCell {
  dueValue: number | null;
  bucket: OilBucket | null;
  unit: string | null;
  status: CellStatus;
}
interface ServiceReportAsset {
  assetId: string;
  fleetNumber: string | null;
  registrationNumber: string | null;
  type: string | null;
  currentValue: number | null;
  measurementUnit: 'Kms' | 'Hours' | null;
  cells: Record<string, ScheduleCell>;  // keyed by scheduleId
  oil: OilCell | null;
  minDelta: number | null;
}
interface PlanGroup {
  planId: string;
  planName: string;
  schedules: ScheduleCol[];
  hasEngineOil: boolean;
  assets: ServiceReportAsset[];
}
interface WofRow {
  assetId: string;
  fleetNumber: string | null;
  registrationNumber: string | null;
  type: string | null;
  expiryDate: string;
  status: 'overdue' | 'upcoming';
}
interface ServiceReportData {
  engineOilDue: number;
  counts: { overdue: number; upcoming: number };
  plans: PlanGroup[];
  wofRows: WofRow[];
}

// Cell colour-coding (Zoho parity).
//   Overdue → red, Upcoming → amber, OK/no-data → muted.
const CELL_STATUS_CLS: Record<CellStatus, string> = {
  overdue:  'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100',
  upcoming: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  ok:       '',
};

// Engine-oil interval buckets — colour darkens with longer intervals so a
// glance at the column tells you which assets are on short-cycle oil plans
// (oil1, light) vs long-cycle (oil5, dark). Matches the Zoho hex values.
const OIL_BUCKET_LABEL: Record<OilBucket, string> = {
  oil1: '≤10k / ≤100h',
  oil2: '≤20k / ≤500h',
  oil3: '≤100k / ≤1000h',
  oil4: '≤200k / ≤2000h',
  oil5: '>200k / >2000h',
};
const OIL_BUCKET_CLS: Record<OilBucket, string> = {
  oil1: 'bg-emerald-200 text-emerald-900 dark:bg-emerald-700/50 dark:text-emerald-50',
  oil2: 'bg-emerald-300 text-emerald-900 dark:bg-emerald-600/60 dark:text-emerald-50',
  oil3: 'bg-emerald-400 text-emerald-900 dark:bg-emerald-600/70 dark:text-emerald-50',
  oil4: 'bg-emerald-500 text-white dark:bg-emerald-700',
  oil5: 'bg-emerald-700 text-white dark:bg-emerald-800',
};

function fmtNum(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString();
}

interface RemindersData {
  service:  { overdue: number; dueSoon: number };
  rego:     { overdue: number; dueSoon: number };
  wof:      { overdue: number; dueSoon: number };
  cof:      { overdue: number; dueSoon: number };
  tracking: { online: number; offline: number };
}

// Single source of truth for the per-asset Service Report payload. Both the
// "Service Reminders" KPI card (overdue/upcoming) and the Service Report tile
// read their counts from THIS one query, so the two numbers can never diverge.
// React Query dedupes by queryKey, so the card and the report share one fetch.
// Includes `location` in the key so switching scope refetches the scoped counts
// (empty querystring when 'all' → whole fleet).
function serviceReportQueryOptions(location: string) {
  const qs = location !== 'all' ? `?location=${encodeURIComponent(location)}` : '';
  return {
    queryKey: ['/dashboard/service-report', location] as const,
    queryFn: async () => (await api<ServiceReportData>(`/dashboard/service-report${qs}`)).data!,
    staleTime: 120_000,
  };
}
export function DashboardPage() {
  // Service-location filter is shared across the whole dashboard — the KPI
  // strip (service/rego/WoF/CoF/tracking) AND the per-asset Service Report
  // scope to it together. Persisted per user via usePreferences so an
  // operator's chosen location sticks across logins. Lifted to the page root
  // (was previously local to ServiceReportTab) so the cards filter too.
  const { value: locationPref, setValue: setLocationPref } =
    usePreferences<string>('dashboard:service-report:location', 'all');
  const location = locationPref ?? 'all';
  const setLocation = (v: string) => setLocationPref(v);
  const locScope = location !== 'all' ? `?location=${encodeURIComponent(location)}` : '';
  // Append the active location to drill-down links so the destination report
  // opens pre-scoped. The card links already carry a `?status=`/`?online=`, so
  // this adds `&location=…`; a no-op when viewing the whole fleet.
  const withLoc = (to: string) => (location !== 'all' ? `${to}&location=${encodeURIComponent(location)}` : to);

  // Service Reminders card reads overdue/upcoming from the SAME service-report
  // query the tile uses (shared via queryKey, deduped to one fetch), so the two
  // numbers are always identical. Previously the card used /dashboard/service-
  // health, which counted overdue differently and diverged from the tile.
  const { data: report } = useQuery(serviceReportQueryOptions(location));
  // staleTime: 60s — reminders change minute-to-minute (cron-driven recompute);
  // a 1-minute cache keeps the strip instant on revisits. Location is in the
  // queryKey so switching scope refetches the scoped counts.
  const { data: reminders } = useQuery({
    queryKey: ['/dashboard/reminders', location],
    queryFn: async () => (await api<RemindersData>(`/dashboard/reminders${locScope}`)).data,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-6">
      <div className="print:hidden">
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Fleet service health, reminders, and per-asset service report.</p>
      </div>
      {/* Single-row fleet-compliance strip. Five dual-count cards covering
          every actionable dimension at a glance: Service (overdue/upcoming),
          Registration, WoF, CoF, Tracking. The previous single-count tiles
          (OK / Due / Unknown / Pending approvals) were dropped — operators
          want the urgent counts up front, not the steady-state OK number.
          `auto-rows-fr` keeps all cells the same height.
          Hidden on print — the printed Service Report is just the per-asset
          schedule grid, not the live KPI strip. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 auto-rows-fr print:hidden">
        <ReminderCard
          title="Service Reminders"
          icon={Wrench}
          overdue={report?.counts.overdue}
          dueSoon={report?.counts.upcoming}
          dueSoonLabel="Upcoming"
          overdueTo={withLoc('/servicing-extras/upcoming-services?serviceDue=overdue')}
          dueSoonTo={withLoc('/servicing-extras/upcoming-services?serviceDue=upcoming')}
        />
        <ReminderCard
          title="Registration Reminders"
          icon={BadgeCheck}
          overdue={reminders?.rego.overdue}
          dueSoon={reminders?.rego.dueSoon}
          overdueTo={withLoc('/compliance/reminders/registration?status=overdue')}
          dueSoonTo={withLoc('/compliance/reminders/registration?status=due')}
        />
        <ReminderCard
          title="WoF Reminders"
          icon={ClipboardCheck}
          overdue={reminders?.wof.overdue}
          dueSoon={reminders?.wof.dueSoon}
          overdueTo={withLoc('/compliance/reminders/wof?status=overdue')}
          dueSoonTo={withLoc('/compliance/reminders/wof?status=due')}
        />
        <ReminderCard
          title="CoF Reminders"
          icon={ClipboardCheck}
          overdue={reminders?.cof.overdue}
          dueSoon={reminders?.cof.dueSoon}
          overdueTo={withLoc('/compliance/reminders/cof?status=overdue')}
          dueSoonTo={withLoc('/compliance/reminders/cof?status=due')}
        />
        <TrackingCard
          online={reminders?.tracking.online}
          offline={reminders?.tracking.offline}
          onlineTo={withLoc('/vehicles/tracker?online=true')}
          offlineTo={withLoc('/vehicles/tracker?online=false')}
        />
      </div>

      {/* Per-asset Service Report — was previously gated behind a separate
          "Service Report" tab; consolidated into the main overview because
          it's the highest-signal view operators want at a glance. Shares the
          page-level service-location filter with the KPI cards above. */}
      <ServiceReportTab location={location} setLocation={setLocation} />
    </div>
  );
}

// ---------- Service Report tab ----------
//
// Mirrors the legacy Zoho "Servicing Report Dashboard". Each asset is one
// row showing the value (km/hr) of its last A/B/C/D service, an Engine
// Oil cell with interval-bucket colour, and the current odo/hour reading.
// Cell background red = overdue, amber = upcoming, plain = OK.
function ServiceReportTab({ location, setLocation }: { location: string; setLocation: (v: string) => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { actions: grantedActions } = useUserActions();
  const canRecompute = grantedActions.has('admin.recompute-upcoming-services');

  // Service-report payload (cron-driven aggregates, 2-min staleTime). This is
  // the SAME query the Service Reminders KPI card uses — shared by queryKey so
  // there's one fetch and the tile's counts always equal the card's. The user
  // can force a refresh via the "Recompute" admin button below.
  const { data: effective, isLoading: loading } = useQuery(serviceReportQueryOptions(location));
  const { data: locations = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['/master-data/service-locations'],
    queryFn: async () => (await api<Array<{ id: string; name: string }>>('/master-data/service-locations?limit=500')).data || [],
    staleTime: 5 * 60_000,
  });

  // Manual fleet-wide recompute — admin trigger surfaced here so a user
  // doesn't have to wait for the 4-hour cron to see IoT-driven meter
  // changes reflected in the table. Disabled if the user lacks the
  // `admin.recompute-upcoming-services` grant.
  const recompute = useMutation({
    mutationFn: async () => {
      const r = await api<{ rowsWritten: number; durationMs: number }>(
        '/admin/recompute-upcoming-services', { method: 'POST' });
      if (!r.success) throw new Error(r.error?.message || 'Recompute failed');
      return r.data!;
    },
    onSuccess: (d) => {
      toast('Service Report refreshed', `${d?.rowsWritten ?? 0} rows updated in ${d?.durationMs ?? 0}ms`, 'success');
      qc.invalidateQueries({ queryKey: ['/dashboard/service-report'] });
    },
    onError: (e: any) => toast('Recompute failed', e.message, 'error'),
  });

  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'all' | CellStatus>('all');
  // `location` / `setLocation` are owned by the parent DashboardPage (and
  // persisted there per user via usePreferences) so the KPI strip and this
  // report scope together. The selector UI below still drives them.
  // Per-plan collapse state — defaults to **collapsed** so the dashboard
  // opens to a compact overview of which plans have urgent work and the
  // user expands only the ones they want to drill into. We track which
  // plans are *expanded* (rather than collapsed) so new plans appearing
  // in the data automatically start collapsed without needing to seed
  // state on every change.
  const [expandedPlans, setExpandedPlans] = React.useState<Set<string>>(new Set());
  function togglePlan(planId: string) {
    setExpandedPlans((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) next.delete(planId);
      else next.add(planId);
      return next;
    });
  }

  // Direct PDF download — no print dialog. html2pdf.js is fetched from a CDN
  // on first use (cached on window after) so we don't bloat the bundle for
  // operators who never hit the download button. Strategy:
  //   1. Expand every plan section so the captured DOM has all tables visible.
  //   2. Toggle the print-only brand header on and the screen-only toolbar off
  //      (html2canvas captures the live DOM, ignoring @media print rules).
  //   3. Hand the wrapper to html2pdf for capture → A4 PDF → file download.
  //   4. Restore everything regardless of success / failure.
  const printContainerRef = React.useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = React.useState(false);

  function loadHtml2Pdf(): Promise<any> {
    const w = window as any;
    if (w.html2pdf) return Promise.resolve(w.html2pdf);
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      s.onload = () => resolve((window as any).html2pdf);
      s.onerror = () => reject(new Error('Failed to load PDF library — check your internet connection.'));
      document.head.appendChild(s);
    });
  }

  async function handleDownloadPdf() {
    const wrapper = printContainerRef.current;
    if (!wrapper || isGeneratingPdf) return;
    setIsGeneratingPdf(true);

    // Snapshot prior expand state + the operator-visible elements so we can
    // restore exactly what was on screen before the capture.
    const prev = expandedPlans;
    const printOnly = wrapper.querySelector<HTMLElement>('[data-pdf="brand-header"]');
    const toolbar = wrapper.querySelector<HTMLElement>('[data-pdf="toolbar"]');
    const filterBar = wrapper.querySelector<HTMLElement>('[data-pdf="filter-bar"]');
    const printOnlyPrevDisplay = printOnly?.style.display ?? '';
    const toolbarPrevDisplay = toolbar?.style.display ?? '';
    const filterBarPrevDisplay = filterBar?.style.display ?? '';

    try {
      const html2pdf = await loadHtml2Pdf();

      // 1. Expand every visible plan so the captured DOM has all tables open.
      const allIds = new Set<string>();
      for (const p of (effective?.plans ?? [])) allIds.add(p.planId);
      setExpandedPlans(allIds);

      // 2. Wait two animation frames so React commits the expansion before
      //    html2canvas walks the DOM.
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      await new Promise((r) => requestAnimationFrame(() => r(null)));

      // 3. Show the print-only brand header; hide the screen toolbar and
      //    the filter bar (the PDF is a static snapshot — filter controls
      //    + the live result counter are not useful in the export).
      if (printOnly) printOnly.style.display = 'block';
      if (toolbar) toolbar.style.display = 'none';
      if (filterBar) filterBar.style.display = 'none';

      const filename = `service-report-${toDateInputValue(new Date())}.pdf`;
      await html2pdf().set({
        margin:      [10, 10, 10, 10],
        filename,
        image:       { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff', logging: false },
        jsPDF:       { unit: 'mm', format: 'a4', orientation: 'portrait' },
        // Don't slice a page in the middle of a table row, a table header,
        // or any element explicitly tagged break-inside-avoid. html2pdf
        // honours these selectors when it cuts the rendered canvas.
        pagebreak:   { mode: ['css', 'legacy'], avoid: ['tr', 'thead', '.break-inside-avoid'] },
      }).from(wrapper).save();
    } catch (e: any) {
      toast('PDF download failed', e?.message || 'Could not generate the PDF.', 'error');
    } finally {
      if (printOnly) printOnly.style.display = printOnlyPrevDisplay;
      if (toolbar) toolbar.style.display = toolbarPrevDisplay;
      if (filterBar) filterBar.style.display = filterBarPrevDisplay;
      setExpandedPlans(prev);
      setIsGeneratingPdf(false);
    }
  }

  const plans = effective?.plans ?? [];
  const wofRows = effective?.wofRows ?? [];

  // Sort state — defaults to Fleet # ascending. Clicking a header cycles
  // through asc → desc → off (cleared). `null` means "use server order",
  // which is worst-first by smallest delta. Applies to every plan table.
  type SortField =
    | 'fleetNumber'
    | 'type'
    | 'current'
    | `schedule:${string}`;
  const [sort, setSort] = React.useState<{ field: SortField; order: 'asc' | 'desc' } | null>(
    { field: 'fleetNumber', order: 'asc' },
  );
  function toggleSort(field: SortField) {
    setSort((prev) => {
      if (!prev || prev.field !== field) return { field, order: 'asc' };
      if (prev.order === 'asc') return { field, order: 'desc' };
      return null;
    });
  }
  function sortedAssets(rows: ServiceReportData['plans'][number]['assets']) {
    if (!sort) return rows;
    const field = sort.field;
    const dir = sort.order === 'asc' ? 1 : -1;
    const fleetValue = (s: string | null): number => {
      // Natural-sort fleet numbers — pull leading digits so "9" < "10".
      const m = (s ?? '').match(/^\d+/);
      return m ? Number(m[0]) : Number.POSITIVE_INFINITY;
    };
    return [...rows].sort((a, b) => {
      let av: number | string = 0; let bv: number | string = 0;
      if (field === 'fleetNumber') {
        av = fleetValue(a.fleetNumber); bv = fleetValue(b.fleetNumber);
      } else if (field === 'type') {
        av = a.type ?? ''; bv = b.type ?? '';
      } else if (field === 'current') {
        av = a.currentValue ?? Number.POSITIVE_INFINITY;
        bv = b.currentValue ?? Number.POSITIVE_INFINITY;
      } else if (field.startsWith('schedule:')) {
        const sid = field.slice('schedule:'.length);
        av = a.cells[sid]?.delta ?? Number.POSITIVE_INFINITY;
        bv = b.cells[sid]?.delta ?? Number.POSITIVE_INFINITY;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
  }
  // Single-character arrow indicators next to sorted column headers.
  const sortIcon = (field: SortField): string =>
    sort && sort.field === field ? (sort.order === 'asc' ? ' ↑' : ' ↓') : '';

  // Free-text + status filter. Status filter checks whether ANY cell on
  // the row carries the matched status — so filtering by Overdue narrows
  // to rows that have at least one overdue service. Empty plan groups
  // are dropped from the output.
  const filteredPlans = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return plans
      .map((p) => ({
        ...p,
        assets: p.assets.filter((a) => {
          if (statusFilter !== 'all') {
            const statuses: CellStatus[] = [
              ...Object.values(a.cells).map((c) => c.status),
              a.oil?.status ?? 'ok',
            ];
            if (!statuses.includes(statusFilter)) return false;
          }
          if (!q) return true;
          return (
            (a.fleetNumber ?? '').toLowerCase().includes(q)
            || (a.registrationNumber ?? '').toLowerCase().includes(q)
            || (a.type ?? '').toLowerCase().includes(q)
            || p.planName.toLowerCase().includes(q)
          );
        }),
      }))
      .filter((p) => p.assets.length > 0);
  }, [plans, statusFilter, search]);

  const totalAssets = plans.reduce((n, p) => n + p.assets.length, 0);
  const filteredAssets = filteredPlans.reduce((n, p) => n + p.assets.length, 0);
  const overdueCount = effective?.counts.overdue ?? 0;
  const upcomingCount = effective?.counts.upcoming ?? 0;
  const engineOilDue = effective?.engineOilDue ?? 0;
  const selectedLocation = locations.find((l) => l.id === location);

  return (
    <>
    {isGeneratingPdf && (
      // Full-screen veil that hides the DOM mutations done during capture
      // (plan expansion, brand-header toggle, toolbar toggle). Sits OUTSIDE
      // printContainerRef so html2canvas doesn't capture it.
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/95 backdrop-blur-sm"
        aria-live="polite"
      >
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          <div className="text-sm font-medium">Generating PDF…</div>
          <div className="text-xs text-muted-foreground">This can take 10–20 seconds.</div>
        </div>
      </div>
    )}
    <div ref={printContainerRef} className="space-y-4 service-report-print">
      {/* ---- Print-only header (logo + company name + title). Mirrors the
              Run Sheet / Service Dashboard print templates so every printed
              report carries the same brand mark and human-readable context.
              The `data-pdf` marker lets the download handler toggle visibility
              for capture (html2canvas ignores @media print). */}
      <div data-pdf="brand-header" className="hidden print:block">
        <div className="text-center">
          <img src={publicAsset('atlas-logo.png')} alt="Atlas" className="mx-auto h-12 object-contain" />
          <div className="mt-2 text-base font-semibold tracking-wide uppercase">Atlas Fleet Maintenance</div>
          <h1 className="mt-4 text-xl font-semibold tracking-wide uppercase">Service Report</h1>
          <div className="mt-1 text-xs text-zinc-600">
            Location: {selectedLocation ? selectedLocation.name : 'All locations'}
            {' · '}
            {filteredAssets} of {totalAssets} asset{totalAssets === 1 ? '' : 's'}
            {' · '}
            Overdue: {overdueCount} · Upcoming: {upcomingCount}
          </div>
          <div className="mt-1 text-[10px] text-zinc-500">
            Printed {fmtDateTime(new Date())}
          </div>
        </div>
        <hr className="my-4 border-zinc-300" />
      </div>

      {/* ---- Header strip: location selector + headline KPIs ---- */}
      <div data-pdf="toolbar" className="flex flex-wrap items-end gap-3 print-hide-toolbar">
        <div className="space-y-1 min-w-[220px]">
          <label className="text-xs uppercase tracking-wide text-muted-foreground">Service Location</label>
          {/* Same searchable popover UX as the Log Service Asset picker.
              LookupField returns null when cleared via "— None —" — we map
              that to the dashboard's 'all' sentinel so existing scope logic
              (`location === 'all'` short-circuits to the fleet-wide query)
              keeps working. */}
          <LookupField
            endpoint="/master-data/service-locations"
            labelKey="name"
            value={location === 'all' ? null : location}
            onChange={(id) => setLocation(id ?? 'all')}
            placeholder="All locations"
          />
        </div>
        <div className="text-xs text-muted-foreground pb-1.5">
          {selectedLocation ? <>Scope: <span className="font-medium text-foreground">{selectedLocation.name}</span></> : 'Scope: whole fleet'}
        </div>
        <div className={cn('flex gap-2 pb-1', !canRecompute && 'ml-auto')}>
          {/* Download as PDF — renders the report client-side via html2pdf
              and triggers a direct file download. No browser print dialog.
              All plan sections are auto-expanded for the capture, then
              restored after. */}
          <Button
            size="sm"
            variant="outline"
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
            title="Generate and download a PDF copy of this report"
          >
            <Printer className="h-3.5 w-3.5 mr-1.5" />
            {isGeneratingPdf ? 'Generating…' : 'Download PDF'}
          </Button>
          {canRecompute && (
            <Button
              size="sm"
              variant="outline"
              disabled={recompute.isPending}
              onClick={() => recompute.mutate()}
              title="Re-run the fleet-wide service computation now"
            >
              <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', recompute.isPending && 'animate-spin')} />
              {recompute.isPending ? 'Refreshing…' : 'Refresh'}
            </Button>
          )}
        </div>
      </div>

      {/* "Engine Oil Due" KPI tile was removed alongside the per-row
          column — keeping the standalone count without the column was
          inconsistent. Grid collapses from 4 → 3 cols on md+ accordingly.
          Hidden on print since the print header already shows the totals. */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 print:hidden">
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === 'overdue' ? 'all' : 'overdue')}
          className={cn(
            'text-left rounded-lg border p-3 transition',
            statusFilter === 'overdue' ? 'ring-2 ring-primary' : 'hover:border-primary/50',
          )}
        >
          <div className="text-[10px] uppercase tracking-wide text-red-700 dark:text-red-300">Services Overdue</div>
          <div className="text-3xl font-bold text-red-700 dark:text-red-300 tabular-nums">{overdueCount}</div>
        </button>
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === 'upcoming' ? 'all' : 'upcoming')}
          className={cn(
            'text-left rounded-lg border p-3 transition',
            statusFilter === 'upcoming' ? 'ring-2 ring-primary' : 'hover:border-primary/50',
          )}
        >
          <div className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">Services Upcoming (30d)</div>
          <div className="text-3xl font-bold text-amber-700 dark:text-amber-300 tabular-nums">{upcomingCount}</div>
        </button>
        <div className="rounded-lg border p-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Assets In Scope</div>
          <div className="text-3xl font-bold tabular-nums">{totalAssets}</div>
        </div>
      </div>

      {/* ---- Filter bar ---- */}
      <div data-pdf="filter-bar" className="flex items-center gap-2 flex-wrap print:hidden">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter Fleet # / Rego / Type / Plan…"
          className="h-9 px-3 rounded-md border bg-background text-sm flex-1 min-w-[220px] max-w-md"
        />
        {statusFilter !== 'all' && (
          <button
            type="button"
            onClick={() => setStatusFilter('all')}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Showing {statusFilter === 'overdue' ? 'Overdue' : 'Upcoming'} only · clear
          </button>
        )}
        <div className="ml-auto text-xs text-muted-foreground">{filteredAssets} of {totalAssets} asset(s)</div>
      </div>

      {/* ---- One table per Service Plan ---- */}
      {loading && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground text-center">Loading service report…</CardContent>
        </Card>
      )}
      {!loading && filteredPlans.length === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground text-center">
            No assets match the current filter.
          </CardContent>
        </Card>
      )}
      {!loading && filteredPlans.map((plan) => {
        const collapsed = !expandedPlans.has(plan.planId);
        // Per-plan urgency counts — # of assets with at least one overdue
        // service, and # with at least one upcoming. These render as
        // pill badges in the section header so operators can see what
        // matters without expanding every plan.
        const overdueAssetCount = plan.assets.filter((a) =>
          Object.values(a.cells).some((c) => c.status === 'overdue')
        ).length;
        const upcomingAssetCount = plan.assets.filter((a) =>
          Object.values(a.cells).some((c) => c.status === 'upcoming')
        ).length;
        return (
        <Card key={plan.planId}>
          {/* Clickable header — pressing anywhere on the strip (except the
              count badge) toggles the table below. Chevron flips for affordance.
              `aria-expanded` keeps screen readers in sync. */}
          <CardHeader
            className="cursor-pointer select-none hover:bg-accent/20 transition-colors"
            onClick={() => togglePlan(plan.planId)}
            role="button"
            aria-expanded={!collapsed}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePlan(plan.planId); }
            }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                {collapsed
                  ? <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  : <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                <CardTitle className="text-base">
                  {selectedLocation ? `${selectedLocation.name} · ` : ''}{plan.planName}
                </CardTitle>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-start">
                {overdueAssetCount > 0 && (
                  <Badge variant="destructive" className="text-[10px]">
                    {overdueAssetCount} overdue
                  </Badge>
                )}
                {upcomingAssetCount > 0 && (
                  <Badge className="text-[10px] bg-amber-100 text-amber-900 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-100 border-transparent">
                    {upcomingAssetCount} upcoming
                  </Badge>
                )}
                <Badge variant="secondary" className="text-[10px]">{plan.assets.length} asset(s)</Badge>
              </div>
            </div>
            {!collapsed && (
              <p className="text-xs text-muted-foreground mt-1">
                Each column = one schedule in this plan. Cell shows the asset reading at its last service of that schedule; colour reflects current status.
              </p>
            )}
          </CardHeader>
          {!collapsed && (
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="text-xs text-muted-foreground border-b bg-muted/40">
                <tr>
                  {/* Sortable column headers — click to cycle through
                      ascending → descending → none. The currently-sorted
                      column shows a ↑ / ↓ indicator next to its label. */}
                  <th
                    className="text-left py-2 px-3 font-medium sticky left-0 bg-muted/40 cursor-pointer select-none hover:text-foreground"
                    onClick={() => toggleSort('fleetNumber')}
                    title="Click to sort by Fleet No"
                  >
                    Fleet No{sortIcon('fleetNumber')}
                  </th>
                  <th
                    className="text-left py-2 px-3 font-medium cursor-pointer select-none hover:text-foreground"
                    onClick={() => toggleSort('type')}
                    title="Click to sort by Type"
                  >
                    Type{sortIcon('type')}
                  </th>
                  {plan.schedules.map((s) => (
                    <th
                      key={s.scheduleId}
                      className="text-right py-2 px-3 font-medium whitespace-nowrap cursor-pointer select-none hover:text-foreground"
                      title={s.serviceInterval ? `Interval: ${s.serviceInterval.toLocaleString()} ${s.unitOfMeasurement ?? ''}\nClick to sort by ${s.scheduleName} due` : `Click to sort by ${s.scheduleName} due`}
                      onClick={() => toggleSort(`schedule:${s.scheduleId}` as SortField)}
                    >
                      {s.scheduleName}{sortIcon(`schedule:${s.scheduleId}` as SortField)}
                      {s.serviceInterval ? (
                        <div className="text-[10px] font-normal text-muted-foreground/70">
                          {s.serviceInterval.toLocaleString()} {s.unitOfMeasurement ?? ''}
                        </div>
                      ) : null}
                    </th>
                  ))}
                  <th
                    className="text-right py-2 px-3 font-medium whitespace-nowrap cursor-pointer select-none hover:text-foreground"
                    onClick={() => toggleSort('current')}
                    title="Click to sort by current odo / hours / hubo"
                  >
                    Current{sortIcon('current')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedAssets(plan.assets).map((a) => (
                  <tr key={a.assetId} className="border-b last:border-0 hover:bg-accent/30">
                    {/* Fleet # cell — opens the asset's detail side-panel on
                        the Assets report. `?view=<id>` is the read-only
                        deep-link (`?edit=<id>` still opens the edit form). */}
                    <td
                      className="py-2 px-3 sticky left-0 bg-background cursor-pointer hover:underline"
                      onClick={() => navigate(`/fleet/assets?view=${encodeURIComponent(a.assetId)}`)}
                      title="Open asset detail"
                    >
                      <div className="font-medium">{a.fleetNumber || '—'}</div>
                      {a.registrationNumber && (
                        <div className="text-[11px] text-muted-foreground">{a.registrationNumber}</div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">{a.type || '—'}</td>
                    {plan.schedules.map((s) => (
                      <ScheduleCellTd
                        key={s.scheduleId}
                        cell={a.cells[s.scheduleId] ?? null}
                        unit={s.unitOfMeasurement}
                        // Drill into ServiceHistory for this asset + service
                        // type. The CrudPage's column-filter params auto-apply
                        // on mount, so the report opens pre-filtered.
                        //
                        // ⚠️ By schedule NAME, not id. Every service plan has
                        // its own "A-Service" row (23 of them fleet-wide), and
                        // an asset that has changed plan keeps its old plan's
                        // schedule id on historical records. Fleet 318 is the
                        // worked example: it sits on Default Vehicle Service
                        // Plan, whose A-Service is …2af5, while all 8 of its
                        // A-Service records carry …2ac8 = A-Service [Trucks].
                        // Linking by id therefore returned zero rows for an
                        // asset with plenty of history. 83 of 340 active
                        // assets are in that position. The name resolves
                        // server-side to every schedule called that
                        // (refTextFilters → $in), and `asset` keeps the result
                        // to this vehicle. See docs/BACKLOG.md.
                        onClick={() => navigate(
                          `/servicing/history?asset=${encodeURIComponent(a.assetId)}`
                          + `&servicePlanSchedule=${encodeURIComponent(s.scheduleName)}`,
                        )}
                      />
                    ))}
                    <td className="py-2 px-3 text-right tabular-nums">
                      {a.currentValue != null
                        ? <>{fmtNum(a.currentValue)} <span className="text-xs text-muted-foreground">{a.measurementUnit ?? ''}</span></>
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
          )}
        </Card>
        );
      })}

      {/* ---- WoF/CoF strip ---- */}
      {wofRows.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {selectedLocation ? `${selectedLocation.name} ` : ''}WoF / CoF Information
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Assets with WoF/CoF expired or expiring in the next 30 days.
            </p>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b bg-muted/40">
                <tr>
                  <th className="text-left py-2 px-3 font-medium">Fleet No</th>
                  <th className="text-left py-2 px-3 font-medium">Type</th>
                  <th className="text-right py-2 px-3 font-medium">WoF/CoF Expiry</th>
                </tr>
              </thead>
              <tbody>
                {wofRows.map((w) => (
                  <tr key={w.assetId} className="border-b last:border-0">
                    <td className="py-2 px-3 font-medium">{w.fleetNumber || '—'}</td>
                    <td className="py-2 px-3 text-muted-foreground">{w.type || '—'}</td>
                    <td className={cn('py-2 px-3 text-right tabular-nums', CELL_STATUS_CLS[w.status])}>
                      {fmtDate(w.expiryDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ---- Colour-code legend ---- */}
      <Card className="print:break-inside-avoid">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Colour Code</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Service status</div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className={cn('px-2 py-1 rounded border', CELL_STATUS_CLS.upcoming)}>Service Upcoming (within 30 days)</span>
              <span className={cn('px-2 py-1 rounded border', CELL_STATUS_CLS.overdue)}>Service Overdue</span>
              <span className="px-2 py-1 rounded border bg-background">No data / OK</span>
            </div>
          </div>
          {/* Engine-oil interval bucket colour-code legend removed —
              the per-row Engine Oil Due column it documented is hidden,
              so the legend was orphaned. */}
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">WoF / CoF</div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className={cn('px-2 py-1 rounded border', CELL_STATUS_CLS.upcoming)}>Expires within 30 days</span>
              <span className={cn('px-2 py-1 rounded border', CELL_STATUS_CLS.overdue)}>Expired</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Print stylesheet — scoped to this page via a runtime <style>. The
          AppShell hides chrome via `print:hidden`; here we tighten the
          report itself: landscape A4, no shadows, forced status-cell
          colours (browsers strip backgrounds by default to save ink). */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 8mm; }
          body { background: white !important; }
          /* AppShell's <main> has overflow-x-hidden for the screen layout —
             in print that clips the right-hand columns of wide tables.
             Force it (and any other overflow-hidden ancestor) back to
             visible so the table extends to the page edge. */
          html, body, main, .overflow-x-hidden {
            overflow: visible !important;
            max-width: 100% !important;
          }
          /* Preserve red/amber cell backgrounds so overdue/upcoming colours
             still convey urgency on paper. Without this, Chrome strips
             backgrounds for ink-saving. */
          .text-red-900, .text-amber-900,
          [class*="bg-red-"], [class*="bg-amber-"] {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* Drop card shadows + borders shrink to single-pixel lines. */
          .shadow, .shadow-sm, .shadow-md, .shadow-lg, .shadow-xl, .shadow-2xl {
            box-shadow: none !important;
          }
          /* Hide the toolbar row (search input, location picker, recompute,
             print button) — operators don't need controls on the printout. */
          .print-hide-toolbar { display: none !important; }

          /* Service Report tables run wide — many plans have 4+ schedule
             columns plus Fleet # / Type / Current. Shrink type + padding so
             the right-hand columns aren't clipped on landscape A4. */
          .service-report-print { font-size: 9px; }
          .service-report-print table {
            font-size: 8px !important;
            line-height: 1.1 !important;
            table-layout: auto !important;
            width: 100% !important;
            max-width: 100% !important;
            border-collapse: collapse !important;
          }
          /* Force every cell to be shrinkable. The "whitespace-nowrap" class
             (used on schedule headers + the Current column) and "tabular-nums"
             were the main culprits - they kept each column wider than
             necessary, pushing the right edge off the page. */
          .service-report-print th,
          .service-report-print td {
            padding: 2px 3px !important;
            word-break: break-word !important;
            white-space: normal !important;
            overflow-wrap: anywhere !important;
            min-width: 0 !important;
            max-width: none !important;
          }
          .service-report-print .whitespace-nowrap { white-space: normal !important; }

          /* The screen layout wraps each table in overflow-x-auto so wide
             tables scroll horizontally. In print there's no scrolling — the
             container would just clip the right-hand columns. Force it back
             to visible so the table renders fully. Same for sticky cells
             (sticky is meaningless without a scroll container). */
          .service-report-print .overflow-x-auto { overflow: visible !important; }
          .service-report-print .sticky {
            position: static !important;
            background: transparent !important;
          }

          /* Each plan section stays as one chunk so a plan header isn't left
             stranded at the bottom of a page from its table. */
          .service-report-print .break-inside-avoid,
          .service-report-print [class*="break-inside-avoid"] {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* Colour-code legend renders LAST and must stay together — put it
             on a fresh page if needed rather than splitting mid-card. */
          .service-report-print > div:last-of-type,
          .service-report-print > .break-inside-avoid {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
    </>
  );
}

// What unit family is this schedule on? Drives whether we format cells
// as dates (time-based) or numbers (distance / hours).
function unitKind(unit: string | null | undefined): 'time' | 'distance' | 'hours' | 'unknown' {
  const u = (unit ?? '').toLowerCase();
  if (/(month|day|year|week)/.test(u)) return 'time';
  if (/(km|kilometre|kilometer|mile|distance)/.test(u)) return 'distance';
  if (/(hour|hr)/.test(u)) return 'hours';
  return 'unknown';
}
function shortDate(iso: string | null): string {
  return iso ? fmtDate(iso, '') : '';
}
function shortUnit(unit: string | null | undefined): string {
  const kind = unitKind(unit);
  if (kind === 'distance') return 'km';
  if (kind === 'hours') return 'h';
  return unit ?? '';
}
// Format the delta into a short human phrase matching the unit family.
function formatDelta(delta: number | null, status: CellStatus, unit: string | null | undefined): string {
  if (delta == null) return '—';
  const kind = unitKind(unit);
  const abs = Math.abs(delta);
  if (kind === 'time') {
    const days = Math.round(abs);
    if (status === 'overdue') return `${days}d overdue`;
    if (days === 0) return 'due today';
    if (days === 1) return 'in 1 day';
    return `in ${days} days`;
  }
  const label = kind === 'hours' ? 'h' : 'km';
  if (status === 'overdue') return `${fmtNum(abs)} ${label} past`;
  return `${fmtNum(abs)} ${label} left`;
}

function ScheduleCellTd({ cell, unit, onClick }: { cell: ScheduleCell | null; unit: string | null; onClick?: () => void }) {
  if (!cell || (cell.lastValue == null && cell.lastDate == null && cell.nextValue == null && cell.nextDate == null && cell.delta == null)) {
    return (
      <td
        className={cn('py-2 px-3 text-right text-muted-foreground/60', onClick && 'cursor-pointer hover:bg-accent/30')}
        onClick={onClick}
        title={onClick ? 'Open service history for this asset / schedule' : undefined}
      >—</td>
    );
  }
  const kind = unitKind(unit);
  const u = shortUnit(unit);

  // Primary line — "Due" phrase. Always reads naturally:
  //   • Time-based:  "Due 15 May 26"
  //   • Distance:    "5,000 km left"  / "1,200 km overdue"
  //   • Hours:       "200 h left"     / "30 h overdue"
  const duePhrase = (() => {
    if (kind === 'time' && cell.nextDate) return `Due ${shortDate(cell.nextDate)}`;
    if (cell.delta != null) return formatDelta(cell.delta, cell.status, unit);
    if (cell.nextValue != null) return `Due ${fmtNum(cell.nextValue)} ${u}`;
    return '—';
  })();

  // Secondary line — always a date so the user can reason about freshness
  // without a unit-table lookup. Falls back to nothing if no history.
  // When a satisfying service is done but awaiting approval, surface that
  // instead so an "overdue" cell reads as "work done, paperwork in flight".
  // A draft outranks the last-serviced date here: if one is sitting there
  // unapproved, that is what someone needs to act on, and the approved date
  // is still a mouseover away.
  const lastPhrase = cell.pendingApproval
    ? '⏳ Pending approval'
    : cell.draftDate
      ? `✎ Draft ${shortDate(cell.draftDate)}`
      : (cell.lastDate ? `Last ${shortDate(cell.lastDate)}` : null);

  // Hover tooltip — full breakdown so the rich data is one mouseover away.
  const tooltip = [
    cell.pendingApproval ? 'A service that satisfies this schedule is completed but awaiting approval — the due date updates once approved.' : null,
    cell.draftDate
      ? `Unapproved service dated ${shortDate(cell.draftDate)}${cell.draftJobSheet ? ` (job sheet ${cell.draftJobSheet})` : ''} — it does not count until approved, so this schedule stays due.`
      : null,
    cell.lastDate ? `Last serviced (approved): ${shortDate(cell.lastDate)}` : null,
    cell.lastValue != null ? `Reading at last service: ${fmtNum(cell.lastValue)} ${u}` : null,
    cell.nextDate ? `Next due: ${shortDate(cell.nextDate)}` : null,
    cell.nextValue != null ? `Next service target: ${fmtNum(cell.nextValue)} ${u}` : null,
  ].filter(Boolean).join('\n');

  return (
    <td
      className={cn('py-2 px-3 align-middle text-right whitespace-nowrap', CELL_STATUS_CLS[cell.status], onClick && 'cursor-pointer hover:brightness-95')}
      title={onClick ? `${tooltip || ''}${tooltip ? '\n' : ''}Click to open service history for this asset / schedule`.trim() : (tooltip || undefined)}
      onClick={onClick}
    >
      <div className="text-[13px] leading-tight font-medium">{duePhrase}</div>
      {lastPhrase && (
        <div className={cn(
          'text-[10px] leading-tight mt-0.5',
          cell.pendingApproval || cell.draftDate ? 'opacity-90 italic' : 'opacity-70',
        )}>{lastPhrase}</div>
      )}
    </td>
  );
}

function OilDueCell({ oil }: { oil: OilCell | null }) {
  if (!oil) {
    return <td className="py-2 px-3 text-right text-muted-foreground/60">—</td>;
  }
  const cls = oil.bucket ? OIL_BUCKET_CLS[oil.bucket] : '';
  return (
    <td className={cn('py-2 px-3 text-right tabular-nums', cls)}>
      {oil.dueValue != null ? <>{fmtNum(oil.dueValue)} <span className="text-xs opacity-80">{oil.unit ?? ''}</span></> : '—'}
    </td>
  );
}

function ReminderCard({
  title, icon: Icon, overdue, dueSoon, overdueTo, dueSoonTo,
  dueSoonLabel = 'Due Soon',
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  overdue?: number;
  dueSoon?: number;
  overdueTo: string;
  dueSoonTo: string;
  /** Right-side label override. Defaults to "Due Soon"; the Service card
   *  uses "Upcoming" to match the legacy service-health bucket name. */
  dueSoonLabel?: string;
}) {
  const total = (overdue ?? 0) + (dueSoon ?? 0);
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        {/* `items-start` + `min-h-[2lh]` on the title text reserves space for
            two lines in every card, so titles that wrap (e.g. "Registration
            Reminders") no longer push the overdue/due-soon body down out of
            alignment with neighbouring cards. */}
        <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground flex items-start gap-2">
          <Icon className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span className="flex-1 leading-tight xl:min-h-[2lh]">{title}</span>
          <span className="text-[10px] text-muted-foreground shrink-0">{total} total</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-2 pt-0">
        <Link to={overdueTo} className="block rounded-md border border-destructive/30 bg-destructive/5 hover:bg-destructive/10 transition px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-destructive">Overdue</div>
          <div className="text-2xl font-bold text-destructive tabular-nums">{overdue ?? '—'}</div>
        </Link>
        <Link to={dueSoonTo} className="block rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 transition px-3 py-2 dark:bg-amber-950/30 dark:border-amber-800">
          <div className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">{dueSoonLabel}</div>
          <div className="text-2xl font-bold text-amber-700 dark:text-amber-300 tabular-nums">{dueSoon ?? '—'}</div>
        </Link>
      </CardContent>
    </Card>
  );
}

function TrackingCard({ online, offline, onlineTo, offlineTo }: {
  online?: number; offline?: number; onlineTo: string; offlineTo: string;
}) {
  const total = (online ?? 0) + (offline ?? 0);
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        {/* Title block mirrors ReminderCard so the body aligns horizontally
            across the row, even when a sibling card's title wraps. */}
        <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground flex items-start gap-2">
          <Radio className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span className="flex-1 leading-tight xl:min-h-[2lh]">Tracking Status</span>
          <span className="text-[10px] text-muted-foreground shrink-0">{total} tracked</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-2 pt-0">
        <Link to={onlineTo} className="block rounded-md border border-green-300 bg-green-50 hover:bg-green-100 transition px-3 py-2 dark:bg-green-950/30 dark:border-green-800">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-green-700 dark:text-green-300">
            <Wifi className="h-3 w-3" /> Online
          </div>
          <div className="text-2xl font-bold text-green-700 dark:text-green-300 tabular-nums">{online ?? '—'}</div>
        </Link>
        <Link to={offlineTo} className="block rounded-md border bg-muted/40 hover:bg-muted transition px-3 py-2">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            <WifiOff className="h-3 w-3" /> Offline
          </div>
          <div className="text-2xl font-bold tabular-nums">{offline ?? '—'}</div>
        </Link>
      </CardContent>
    </Card>
  );
}
